"use client";
import * as LucideIcons from 'lucide-react';
import * as FaIcons from 'react-icons/fa6';
const Icons = { ...LucideIcons, ...FaIcons };
import Link from 'next/link';
import { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { createUpdateHandler } from "./component-helpers";
import styles from "./osm-banner.module.css";
import { componentDefaults } from "./data";


const openDialog = (id) => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('lunar:open-dialog', { detail: { id } }));
  }
};

const BuilderSection = ({ tagName = 'div', className, innerContainer, fullWidth, style, children, id, sectionId, isVisible = true, removePaddingLeft, removePaddingRight, onUpdate, ...rest }) => {
  if (!isVisible) return null;
  const Tag = tagName;
  const normalizedSectionId = (sectionId && typeof sectionId === 'string') ? sectionId.replace(/-+$/, '') : '';
  let finalId = id || normalizedSectionId;
  finalId = finalId ? finalId.replace(/-+/g, '-') : undefined;
  const containerClasses = ["container-grid"];
  if (removePaddingLeft === true || removePaddingLeft === "true") containerClasses.push("pl-0");
  if (removePaddingRight === true || removePaddingRight === "true") containerClasses.push("pr-0");
  if (fullWidth === true || fullWidth === "true") containerClasses.push("container-full");
  const containerClass = containerClasses.join(" ");
  if (innerContainer) {
    return (
      <Tag id={finalId} className={className} style={style}>
        <div className={containerClass}>{children}</div>
      </Tag>
    );
  }
  return <Tag id={finalId} className={containerClass + " " + (className || '')} style={style}>{children}</Tag>;
};

const BuilderText = ({ tagName = 'p', content, className, style, children, id, sectionId, suffix, isVisible = true, tooltipIfTruncated, onUpdate, ...rest }) => {
  if (!isVisible) return null;
  const Tag = tagName;
  const normalizedSectionId = (sectionId && typeof sectionId === 'string') ? sectionId.replace(/-+$/, '') : '';
  const effectiveSuffix = suffix || (className ? className.split(' ')[0] : tagName);
  let finalId = id || (normalizedSectionId ? normalizedSectionId + '-' + effectiveSuffix : undefined);
  finalId = finalId ? finalId.replace(/-+/g, '-') : undefined;
  const finalClassName = ("builder-text " + (className || '') + " " + (!content && !children ? 'empty-builder-text' : '')).trim();
  const title = tooltipIfTruncated ? content : undefined;
  const isSimpleString = content && typeof content === 'string' && !/<[a-z]|&[a-z0-9#]+;/i.test(content);
  return (
    <Tag
      id={finalId}
      className={finalClassName}
      style={style}
      dangerouslySetInnerHTML={content && !isSimpleString ? { __html: content } : undefined}
      title={title}
    >
      {isSimpleString ? content : (!content ? children : null)}
    </Tag>
  );
};

const BuilderButton = ({ label, href, openInNewTab, className, style, children, linkType, targetDialogId, id, sectionId, suffix, iconLeft, iconRight, hideLabel, isVisible = true, onUpdate, isSubmit, disabled, isLoading, onClick, disableLinkSettings, ...rest }) => {
  if (!isVisible) return null;
  const normalizedSectionId = (sectionId && typeof sectionId === 'string') ? sectionId.replace(/-+$/, '') : '';
  let finalId = id || (normalizedSectionId && suffix ? normalizedSectionId + '-' + suffix : undefined);
  finalId = finalId ? finalId.replace(/-+/g, '-') : undefined;
  const renderIcon = (icon) => {
    if (typeof icon === 'string' && typeof Icons !== 'undefined' && Icons[icon]) {
      const IconComponent = Icons[icon];
      return <IconComponent style={{ width: '1.25rem', height: '1.25rem' }} />;
    }
    return typeof icon === 'string' ? null : icon;
  };
  const isSimpleLabel = label && typeof label === 'string' && !/<[a-z]|&[a-z0-9#]+;/i.test(label);
  const content = (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', gap: 'inherit', opacity: isLoading ? 0.5 : 1 }}>
         {isLoading && <div className="spinner-sm" style={{ marginRight: '8px' }}></div>}
         {!isLoading && renderIcon(iconLeft) && <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{renderIcon(iconLeft)}</span>}
          {!hideLabel && (
             <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {isSimpleLabel ? label : (label && typeof label === 'string' ? <span dangerouslySetInnerHTML={{ __html: label }} /> : (label || children))}
             </div>
          )}
         {!isLoading && renderIcon(iconRight) && <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{renderIcon(iconRight)}</span>}
      </div>
  );
  const finalClassName = className ? (className.split(' ').includes('btn') ? className : ('btn ' + className)) : 'btn';
  const isLink = !!href || (linkType === 'dialog' && !!targetDialogId);
  if (isSubmit) {
    return <button id={finalId} type="submit" disabled={disabled || isLoading} className={finalClassName} style={{ ...style, textDecoration: 'none' }} onClick={onClick}>{content}</button>;
  }
  if (!isLink) {
    return <button id={finalId} type="button" disabled={disabled || isLoading} className={finalClassName} style={{ ...style, textDecoration: 'none' }} onClick={onClick}>{content}</button>;
  }
  const isNewTab = openInNewTab === true || openInNewTab === 'true';
  const targetAttr = isNewTab ? '_blank' : undefined;
  const relAttr = isNewTab ? 'noopener noreferrer' : 'noopener';

  if (linkType === 'dialog' && targetDialogId) {
    return (
      <a id={finalId} href="#" className={finalClassName} style={{ ...style, cursor: 'pointer', textDecoration: 'none' }} rel={relAttr} onClick={(e) => { e.preventDefault(); if (onClick) onClick(e); openDialog(targetDialogId); }}>{content}</a>
    );
  }
  return <a id={finalId} href={href || '#'} target={targetAttr} className={finalClassName} style={{ ...style, textDecoration: 'none' }} rel={relAttr} onClick={onClick}>{content}</a>;
};
export default function OsmBanner({
title = "Information Banner",
buttonText = componentDefaults["osm-banner"].buttonText,
buttonUrl = componentDefaults["osm-banner"].buttonUrl,
buttonOpenInNewTab = componentDefaults["osm-banner"].buttonOpenInNewTab || false,
buttonLinkType = componentDefaults["osm-banner"].buttonLinkType || "url",
buttonTargetDialogId = componentDefaults["osm-banner"].buttonTargetDialogId,
buttonIconLeft = componentDefaults["osm-banner"].buttonIconLeft,
buttonIconRight = componentDefaults["osm-banner"].buttonIconRight,
buttonId,
buttonVisible = componentDefaults["osm-banner"].buttonVisible,
variant = componentDefaults["osm-banner"].variant,
isOverlay, // Added
onUpdate,
sectionId,
className = "",
isVisible = true,
fullWidth,
removePaddingLeft,
removePaddingRight
}) {
const update = createUpdateHandler(onUpdate);
const [isClosed, setIsClosed] = useState(false);
const [isMarquee, setIsMarquee] = useState(false);
const [marqueeOffset, setMarqueeOffset] = useState(0);
const [marqueeDuration, setMarqueeDuration] = useState(10); // Default duration
const textWrapperRef = useRef(null);
const textRef = useRef(null);
useEffect(() => {
const checkOverflow = () => {
if (textWrapperRef.current && textRef.current) {
const wrapper = textWrapperRef.current;
const scrollWidth = wrapper.scrollWidth;
const clientWidth = wrapper.clientWidth;
if (scrollWidth > clientWidth) {
setIsMarquee(true);
setMarqueeOffset(scrollWidth - clientWidth);
const duration = Math.max(8, (scrollWidth - clientWidth) / 50);
setMarqueeDuration(duration);
} else {
setIsMarquee(false);
setMarqueeOffset(0);
}
}
};
checkOverflow();
window.addEventListener('resize', checkOverflow);
return () => window.removeEventListener('resize', checkOverflow);
}, [title, buttonVisible]); // Re-check when content changes
if (isClosed || isVisible === false) return null;
return (
<BuilderSection
tagName="div"
className={`${styles.banner} ${styles[variant]} z-content-1 ${className}`}
sectionId={sectionId}
isOverlay={isOverlay}
showFullWidthControl={false}
fullWidth={true}
showVariantToggle={true}
variant={variant}
variants={["neutral", "brand"]}
onVariantChange={update('variant')}
>
<div className="container-grid">
<div className="grid align-center">
<div className={`col-mobile-4 col-tablet-8 col-desktop-12 ${styles.content}`}>
<div
className={`${styles.marqueeWrapper} ${isMarquee ? styles.marqueeMask : ''}`}
ref={textWrapperRef}
>
<div
ref={textRef}
style={{
display: isMarquee ? 'inline-block' : 'block',
width: isMarquee ? 'auto' : '100%',
'--marquee-offset': `-${marqueeOffset}px`,
'--marquee-duration': `${marqueeDuration}s`
}}
>
<BuilderText
tagName="p"
className={`body-regular ${styles.title} ${isMarquee ? styles.marquee : ''}`}
content={title}
onChange={update('title')}
sectionId={sectionId}
multiline={false}
suffix="title"
/>
</div>
</div>
{buttonVisible && (
<BuilderButton
label={buttonText}
href={buttonUrl}
openInNewTab={buttonOpenInNewTab}
onOpenInNewTabChange={update('buttonOpenInNewTab')}
isVisible={buttonVisible}
sectionId={sectionId}
className="btn btn-outline btn-sm"
linkType={buttonLinkType}
targetDialogId={buttonTargetDialogId}
iconLeft={buttonIconLeft}
iconRight={buttonIconRight}
onIconLeftChange={update('buttonIconLeft')}
onIconRightChange={update('buttonIconRight')}
id={buttonId}
suffix="button"
/>
)}
<div className={`${styles.actions} z-content-1`}>
<button
className="btn btn-icon btn-neutral btn-sm"
onClick={() => setIsClosed(true)}
aria-label="Close banner"
>
<X style={{ width: 20, height: 20 }} />
</button>
</div>
</div>
</div>
</div>
</BuilderSection>
);
}